import { jsx } from 'react/jsx-runtime';
import classNames from 'classnames';
import { Image } from '../image/image.esm.js';
import { LottieAnimation } from '../lottie-animation/lottie-animation.esm.js';
import { Video } from '../video/video.esm.js';
import { convertMediaToLottie } from './utils/convert-media-to-lottie.esm.js';
import { getCornerRadiusClasses } from './utils/get-corner-radius-classes.esm.js';
import { isLottieJson } from './utils/is-lottie-json.esm.js';
import { isVideoExtension } from './utils/is-video-extension.esm.js';
import { isYoutubeVideoUrl } from './utils/is-youtube-video-url.esm.js';
import { parseYoutubeVideoId } from './utils/parse-youtube-video-id.esm.js';

const ASPECT_RATIO_16x9 = '56.25%';
const Media = (props)=>{
    const { priority, unoptimized, style, disableRatio = false, hideBorder = false, className, aspectRatio = 'auto', objectFit = 'contain', objectPosition, draggable, autoPlay = true, cornerRadiuses, cornerRadiusSize, onLoad } = props;
    const { url, width, height, title, controls, id, loop } = props.media;
    const isLottie = isLottieJson(url);
    const isVideo = isVideoExtension(url);
    const isYoutubeVideo = isYoutubeVideoUrl(url);
    const disableAspectRatio = isLottie || disableRatio;
    const renderContent = ()=>{
        if (isLottie) {
            return jsx(LottieAnimation, {
                animation: convertMediaToLottie(props),
                onAnimationLoad: onLoad,
                "data-testid": "media-lottie"
            });
        }
        if (isYoutubeVideo) {
            const embedId = parseYoutubeVideoId(url);
            let youtubeVideoSrc = `https://www.youtube.com/embed/${embedId}`;
            if (autoPlay) {
                youtubeVideoSrc += '?autoplay=1';
            }
            return jsx("iframe", {
                width: width,
                height: height,
                src: youtubeVideoSrc,
                frameBorder: "0",
                allow: "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture",
                allowFullScreen: true,
                title: title,
                onLoad: onLoad
            });
        }
        if (isVideo) {
            return jsx("video", {
                loop: loop,
                playsInline: true,
                autoPlay: autoPlay,
                // Video can only be auto played if it is muted
                muted: autoPlay,
                controls: controls,
                width: width,
                height: height,
                className: "d-block w-100",
                onLoad: onLoad,
                children: jsx("source", {
                    src: url
                })
            });
        }
        if (id) {
            return jsx(Video, {
                ...props.media,
                id: id,
                onLoad: onLoad
            });
        }
        return jsx(Image, {
            priority: priority,
            unoptimized: unoptimized,
            "data-testid": "media-image",
            src: url || '',
            width: width,
            height: height,
            alt: title || '',
            draggable: draggable,
            onLoad: onLoad,
            style: {
                objectFit,
                objectPosition
            }
        });
    };
    const autoAspectRatio = Number(height) / Number(width) * 100;
    const mergedStyles = {
        ...style
    };
    if (aspectRatio === 'auto' && !isNaN(autoAspectRatio)) {
        mergedStyles['--wcds-aspect-ratio'] = `${autoAspectRatio}%`;
    }
    /**
     * Setting defualt Wistia aspect ratio to 16x9
     */ if (id && aspectRatio === 'auto') {
        mergedStyles['--wcds-aspect-ratio'] = ASPECT_RATIO_16x9;
    }
    let mediaType = 'image';
    if (isLottie) {
        mediaType = 'lottie';
    }
    if (isVideo || isYoutubeVideo || id) {
        mediaType = 'video';
    }
    return jsx("div", {
        "data-testid": "media",
        "data-media-type": mediaType,
        className: classNames('position-relative', {
            ratio: !disableAspectRatio,
            [`ratio-${aspectRatio}`]: aspectRatio !== 'auto' && !disableAspectRatio,
            'border-rounded border-rounded-xl bg-section-3': !hideBorder
        }, className, getCornerRadiusClasses(cornerRadiuses, cornerRadiusSize)),
        style: mergedStyles,
        children: renderContent()
    });
};

export { Media };
